import { isFedRamp } from '@atlassian/atl-context';
import { useApiKeyManagementInfo } from '@atlassian/jira-eoc-api-key-management/src/controllers/index.tsx';
import { useOgTenantInfo } from '@atlassian/jira-eoc-tenant-info/src/controllers/index.tsx';
import { ff } from '@atlassian/jira-feature-flagging';
import { fg } from '@atlassian/jira-feature-gating';
import { useNavigationGlobalResource } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/common/navigation-global-resource/index.tsx';
import { hasItemInSidebar } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/utils/index.tsx';
import { withThrowOnError } from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/throw-when-no-data-and-error/index.tsx';
import { useByokComplianceSettingWithInitialisation } from '@atlassian/jira-products-jsm-compliance-main/src/services/byok-compliance-setting/index.tsx';
import { SERVICE_DESK, SOFTWARE } from '@atlassian/jira-shared-types/src/application-key.tsx';
import {
	FREE_EDITION,
	STANDARD_EDITION,
	UNLICENSED,
} from '@atlassian/jira-shared-types/src/edition.tsx';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import type { UseFeaturesReturn } from './types.tsx';

export const useFeatures = withThrowOnError((): UseFeaturesReturn => {
	const { data, loading, error } = useNavigationGlobalResource();
	const {
		shouldUseJSM,
		shouldAccessUnlockOperations,
		isTransitioning,
		loading: ogTenantInfoLoading,
		isOpsAdmin,
	} = useOgTenantInfo();
	const tenantContext = useTenantContext();
	const appEditions = useAppEditions();
	const { hasKeys, loading: apiKeyManagementLoading } = useApiKeyManagementInfo();
	const { isByokCustomer } = useByokComplianceSettingWithInitialisation();

	// Lets wait for tenant ConsolidationState to load before rendering product's setting side-nav
	if (loading || !data || ogTenantInfoLoading || apiKeyManagementLoading) {
		return {
			data: null,
			loading: loading || ogTenantInfoLoading || apiKeyManagementLoading,
			error,
		};
	}

	const hasJsmAboveStandard =
		!!tenantContext.licensedProducts[SERVICE_DESK] &&
		appEditions.serviceDesk !== FREE_EDITION &&
		appEditions.serviceDesk !== STANDARD_EDITION;

	const hasJsmAboveFree =
		!!tenantContext.licensedProducts[SERVICE_DESK] && appEditions.serviceDesk !== FREE_EDITION;

	const isAdmin = fg('operations-tenant-info-endpoint-update')
		? tenantContext.isSiteAdmin || tenantContext.isAdmin || isOpsAdmin
		: tenantContext.isSiteAdmin || tenantContext.isAdmin;

	return {
		data: {
			hasApplicationAccess: hasItemInSidebar(data, 'application_access_browser'),
			hasSoftwareSection: tenantContext.isAdmin, // software section is only shown to admin users
			hasSoftwareConfig: tenantContext.isAdmin,
			hasServiceDeskSection: hasItemInSidebar(data, 'sd-plugin-admin-section'),
			hasServiceDeskConfig: hasItemInSidebar(data, 'sd-configuration-link'),
			hasServiceDeskEmailRequests: hasItemInSidebar(data, 'sd-admin-mail-info-link'),
			hasServiceDeskIncidentManagementTools: hasItemInSidebar(
				data,
				'sd-incident-management-tools-link',
			),
			hasIntegrationsSection: hasItemInSidebar(data, 'admin_applications_integrations_section'),
			hasServiceDeskCustomerAccess: tenantContext.isAdmin && appEditions.serviceDesk !== UNLICENSED,
			hasServiceDeskKbPermissionsAccess: tenantContext.isAdmin,
			hasServiceDeskAuthenticationSettings:
				tenantContext.isAdmin && appEditions.serviceDesk !== UNLICENSED,
			hasApplicationLinksIntegration: hasItemInSidebar(data, 'configure-application-links'),
			hasDvcsAccountsIntegration: hasItemInSidebar(data, 'bitbucket_bulk_repo'),
			hasStatusPageIntegration:
				!!tenantContext.atlassianAccountId && hasItemInSidebar(data, 'jsd-status-page__admin-page'),
			hasCompassPageIntegration:
				tenantContext.isSiteAdmin && !!tenantContext.licensedProducts[SOFTWARE],
			hasConfluencePageIntegration: tenantContext.isAdmin,
			hasPortfolioAdmin: hasItemInSidebar(data, 'jpo-ws-main-admin'),
			hasProductDiscoverySection:
				tenantContext.isAdmin && appEditions.productDiscovery !== UNLICENSED,
			hasOperationsSection: fg('operations-tenant-info-endpoint-update')
				? (tenantContext.isAdmin || isOpsAdmin) && !!tenantContext.licensedProducts[SERVICE_DESK]
				: tenantContext.isAdmin && !!tenantContext.licensedProducts[SERVICE_DESK],
			hasOpsReportsAccess: fg('operations-tenant-info-endpoint-update')
				? (tenantContext.isAdmin || isOpsAdmin) &&
					!isByokCustomer &&
					shouldUseJSM &&
					!!tenantContext.licensedProducts[SERVICE_DESK] &&
					appEditions.serviceDesk !== FREE_EDITION
				: tenantContext.isAdmin &&
					!isByokCustomer &&
					shouldUseJSM &&
					!!tenantContext.licensedProducts[SERVICE_DESK] &&
					appEditions.serviceDesk !== FREE_EDITION,
			hasChatOpsToolAccess: fg('operations-tenant-info-endpoint-update')
				? (tenantContext.isAdmin || isOpsAdmin) &&
					!!tenantContext.licensedProducts[SERVICE_DESK] &&
					!isFedRamp()
				: tenantContext.isAdmin && !!tenantContext.licensedProducts[SERVICE_DESK] && !isFedRamp(),
			hasAuditLogsAccess: shouldUseJSM && !!tenantContext.licensedProducts[SERVICE_DESK],
			hasGlobalHeartbeatsAccess:
				shouldUseJSM && hasJsmAboveStandard && isAdmin && fg('global_heartbeats'),
			hasGlobalSyncAccess: shouldUseJSM && hasJsmAboveStandard,
			hasGlobalSchedulesAccess:
				shouldUseJSM && hasJsmAboveFree && isAdmin && fg('global_on-call_schedules'),
			hasGlobalIncomingCallAccess: shouldUseJSM && hasJsmAboveStandard && isAdmin,
			hasGlobalIntegrationAccess: shouldUseJSM && hasJsmAboveStandard,
			hasPoliciesAccess: shouldUseJSM && hasJsmAboveStandard,
			hasMaintenanceAccess: shouldUseJSM && !!tenantContext.licensedProducts[SERVICE_DESK],
			hasGlobalEscalationsAccess:
				shouldUseJSM && hasJsmAboveFree && isAdmin && fg('alx-9154_global_escalations'),
			hasGlobalRoleAccess:
				shouldUseJSM && hasJsmAboveStandard && (tenantContext.isAdmin || tenantContext.isSiteAdmin),
			hasRoleCreationAccess:
				shouldUseJSM &&
				hasJsmAboveStandard &&
				(tenantContext.isAdmin || tenantContext.isSiteAdmin || isOpsAdmin) &&
				fg('operations-tenant-info-endpoint-update'),
			hasRoleAssignmentAccess:
				shouldUseJSM &&
				(tenantContext.isAdmin || tenantContext.isSiteAdmin || isOpsAdmin) &&
				fg('operations-tenant-info-endpoint-update'),
			hasRoleBasedNotificationsAccess: fg('operations-tenant-info-endpoint-update')
				? shouldUseJSM && (tenantContext.isAdmin || isOpsAdmin) && hasJsmAboveStandard
				: shouldUseJSM && tenantContext.isAdmin && hasJsmAboveStandard,
			hasOrganizationsListAccess: fg('operations-tenant-info-endpoint-update')
				? !!tenantContext.licensedProducts[SERVICE_DESK] && (tenantContext.isAdmin || isOpsAdmin)
				: !!tenantContext.licensedProducts[SERVICE_DESK] && tenantContext.isAdmin,
			hasUnlockOperationsAccess: fg('operations-tenant-info-endpoint-update')
				? ff('opsgenie-consolidation-transitioning-state_d7vm9') &&
					shouldAccessUnlockOperations &&
					(tenantContext.isAdmin || tenantContext.isSiteAdmin || isOpsAdmin)
				: ff('opsgenie-consolidation-transitioning-state_d7vm9') &&
					shouldAccessUnlockOperations &&
					(tenantContext.isAdmin || tenantContext.isSiteAdmin),
			hasApiKeyManagementAccess: fg('operations-tenant-info-endpoint-update')
				? isTransitioning &&
					shouldUseJSM &&
					hasJsmAboveStandard &&
					(tenantContext.isSiteAdmin || isOpsAdmin) &&
					hasKeys
				: isTransitioning &&
					shouldUseJSM &&
					hasJsmAboveStandard &&
					tenantContext.isSiteAdmin &&
					hasKeys,
		},
		loading,
		error,
	};
});
